/**
 * TimeOfDayDial — a small SVG clock/sun-moon dial for the scoreboard's
 * time-of-day chip, replacing the old emoji (☀/🌆/🌙).
 *
 * The hour only ADVANCES day → twilight → night (engine rule), and failing
 * light docks every SHOT's skill by the index (day 0 / twilight −1 / night −2),
 * nullified by an indoor (lit) stadium. The dial reflects that: the light source
 * rides high (day) → onto the horizon (twilight) → a crescent moon (night), all
 * inside a faint clock ring with quarter ticks. Indoors the venue reads "lit" —
 * a brighter ring + bulb glow — since the penalty doesn't apply.
 *
 * Self-contained, themed with the app's CSS variables (var(--ec-*)) and the dark
 * ink stroke, matching the HandCard glyph conventions.
 */
export function TimeOfDayDial({
  index,
  indoor = false,
  name,
}: {
  /** 0 = day, 1 = twilight, 2 = night. */
  index: number;
  /** Indoor (lit) venue — the time penalty is nullified, so the dial reads "lit". */
  indoor?: boolean;
  /** Optional label for the accessible title. */
  name?: string;
}) {
  const hour = index >= 2 ? 'night' : index === 1 ? 'twilight' : 'day';
  const ringColor = indoor ? 'var(--ec-brass-2)' : 'var(--ec-brass)';
  const title =
    (name ?? (hour === 'night' ? 'Night' : hour === 'twilight' ? 'Twilight' : 'Day')) +
    (indoor ? ' — indoor, lit' : '');

  return (
    <svg
      viewBox="-16 -16 32 32"
      className="inline-block h-[1.25em] w-[1.25em] shrink-0 align-[-0.28em]"
      role="img"
      aria-label={title}
    >
      <title>{title}</title>

      {/* Clock face — faint ring + quarter ticks. */}
      <circle cx={0} cy={0} r={13.5} fill="none" stroke={ringColor} strokeOpacity={indoor ? 0.85 : 0.5} strokeWidth={1.3} />
      {indoor && (
        // Stadium-light halo — the venue is lit, no failing-light penalty.
        <circle cx={0} cy={0} r={13.5} fill="none" stroke="var(--ec-brass-2)" strokeOpacity={0.3} strokeWidth={4} />
      )}
      {[0, 90, 180, 270].map((deg) => {
        const r = (deg * Math.PI) / 180;
        const sx = Math.sin(r) * 13.5;
        const sy = -Math.cos(r) * 13.5;
        const ex = Math.sin(r) * 10.5;
        const ey = -Math.cos(r) * 10.5;
        return <line key={deg} x1={sx} y1={sy} x2={ex} y2={ey} stroke={ringColor} strokeOpacity={0.6} strokeWidth={1} />;
      })}

      {hour === 'day' && <Sun />}
      {hour === 'twilight' && <TwilightSun />}
      {hour === 'night' && <Moon />}
    </svg>
  );
}

/** Full sun high in the dial — brightest, no penalty. */
function Sun() {
  const rays = Array.from({ length: 8 }, (_, i) => {
    const a = (i * 45 * Math.PI) / 180;
    return (
      <line
        key={i}
        x1={Math.sin(a) * 6.5}
        y1={-Math.cos(a) * 6.5}
        x2={Math.sin(a) * 9.5}
        y2={-Math.cos(a) * 9.5}
        stroke="var(--ec-brass-2)"
        strokeWidth={1.4}
        strokeLinecap="round"
      />
    );
  });
  return (
    <>
      {rays}
      <circle cx={0} cy={0} r={4.6} fill="var(--ec-brass-2)" stroke="#13161b" strokeWidth={0.5} />
    </>
  );
}

/** Sun sinking onto the horizon — long shadows, the light begins to fail. */
function TwilightSun() {
  return (
    <>
      {/* Horizon line. */}
      <line x1={-11} y1={4} x2={11} y2={4} stroke="var(--ec-brass-deep)" strokeWidth={1.3} strokeLinecap="round" />
      {/* Half-sun resting on the horizon. */}
      <path d="M-6 4 A6 6 0 0 1 6 4 Z" fill="var(--ec-brass-deep)" stroke="#13161b" strokeWidth={0.5} />
      {/* A few low rays. */}
      {[-40, 0, 40].map((deg) => {
        const a = (deg * Math.PI) / 180;
        return (
          <line
            key={deg}
            x1={Math.sin(a) * 7.5}
            y1={4 - Math.cos(a) * 7.5}
            x2={Math.sin(a) * 10.5}
            y2={4 - Math.cos(a) * 10.5}
            stroke="var(--ec-brass-deep)"
            strokeWidth={1.2}
            strokeLinecap="round"
          />
        );
      })}
    </>
  );
}

/** Crescent moon + a couple of stars — night, deepest skill penalty. */
function Moon() {
  return (
    <>
      {/* Crescent carved as a single path so it works on any background. */}
      <path
        d="M4 -7 A7 7 0 1 0 4 7 A5.2 5.2 0 1 1 4 -7 Z"
        fill="var(--ec-pos-ruck)"
        stroke="#13161b"
        strokeWidth={0.5}
      />
      {/* Stars. */}
      <circle cx={-6} cy={-6} r={1.1} fill="var(--ec-bone)" />
      <circle cx={-8.5} cy={1} r={0.8} fill="var(--ec-bone)" />
    </>
  );
}
